import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { CompassIcon } from '@treams/treams-icons';
import { Accordion } from '@treams/treams-ui/common/Accordion';
import { Container } from '@treams/treams-ui/common/Container';
import { Empty } from '@treams/treams-ui/common/Empty';
import { Skeleton } from '@treams/treams-ui/common/Skeleton';
import { Breakpoint } from '@treams/treams-ui/helpers/Breakpoint';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { useGrowthPathContext } from '../contexts/GrowthPathContext';
import { GrowthPathData } from '../GrowthPathConverter';
import GrowthPathChart from './GrowthPathChart';
import GrowthPathCompetenciesList, {
	GrowthPathReceivedFeedbackList,
} from './GrowthPathCompetenciesList';
import GrowthPathCompetencyLabelTabs from './GrowthPathCompetencyLabelTabs';

const Wrapper = styled(Box)`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
`;

const ContentWrapper = styled(Box)`
	display: flex;
	align-items: flex-start;
	gap: ${tokens.gap.page}px;
	width: 100%;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		flex-direction: column;
	}
`;

const CompetencyColumn = styled(Box)`
	flex: 1 1 0;
	min-width: 0;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		order: 2;
		width: 100%;
	}
`;

const ChartColumn = styled(Box)`
	flex: 1 1 0;
	min-width: 0;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		order: 1;
		width: 100%;
	}
`;

const ChartCard = styled(Box)`
	box-sizing: border-box;
	width: 100%;
	padding: ${tokens.padding.section}px;
	background: ${tokens.surface.secondary};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.md}px;

	@media screen and (min-width: ${Breakpoint.Medium}) {
		position: sticky;
		top: ${tokens.padding.container}px;
	}
`;

const EmptyState = styled.div`
	display: flex;
	justify-content: center;
	padding-top: ${tokens.gap.page}px;
`;

type Props = {
	data: GrowthPathData;
	receivedFeedback?: GrowthPathReceivedFeedbackList;
};

const GrowthPathDetailBody: FC<Props> = ({ data, receivedFeedback }) => {
	const { t } = useTranslation();

	const { labelID } = useGrowthPathContext();

	if (data.jobCompetencies.length === 0) {
		return (
			<GrowthPathEmpty
				title={t('growth.path.detail.body.empty.title')}
				description={t('growth.path.detail.body.empty.description')}
			/>
		);
	}

	return (
		<Wrapper>
			<Container wide>
				<ContentWrapper>
					<CompetencyColumn>
						<GrowthPathCompetenciesList
							data={data}
							labelID={labelID}
							receivedFeedback={receivedFeedback}
						/>
					</CompetencyColumn>

					<ChartColumn>
						<ChartCard>
							<GrowthPathCompetencyLabelTabs data={data} />
							<GrowthPathChart data={data} labelID={labelID} />
						</ChartCard>
					</ChartColumn>
				</ContentWrapper>
			</Container>
		</Wrapper>
	);
};

export function GrowthPathEmpty(props: { title: string; description: string }) {
	return (
		<Container wide>
			<EmptyState>
				<Empty icon={<CompassIcon />} title={props.title} description={props.description} />
			</EmptyState>
		</Container>
	);
}

export const GrowthPathDetailBodySkeleton = () => {
	return (
		<Wrapper>
			<Container wide>
				<ContentWrapper>
					<CompetencyColumn>
						<Skeleton.Repeat count={3}>
							<Box py={4}>
								<Skeleton.Box height={24} width={250} />
								<Box mt={2}>
									<Skeleton.Repeat count={4}>
										<Accordion.Skeleton borderless />
									</Skeleton.Repeat>
								</Box>
							</Box>
						</Skeleton.Repeat>
					</CompetencyColumn>

					<ChartColumn>
						<ChartCard>
							<Skeleton.Box height={33} width="100%" />
							<Skeleton.Box mt={3} height={240} width="100%" />
						</ChartCard>
					</ChartColumn>
				</ContentWrapper>
			</Container>
		</Wrapper>
	);
};

export default GrowthPathDetailBody;
